import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { colors, inr } from "@/theme";
import { SectionTitle, EmptyState } from "@/components/common";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useMediaQuery";

export default function Cart() {
  const { items, updateQty, remove, subtotal, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  if (items.length === 0) {
    return (
      <div className="container-max" style={{ padding: "24px 16px" }}>
        <EmptyState title="Your cart is beautifully empty" message="Discover our collections and find something you love." action={<Link to="/" className="btn-primary" style={{ textDecoration: "none" }}>Discover</Link>} />
      </div>
    );
  }

  const goCheckout = () => navigate(user ? "/checkout" : "/login", { state: { from: "/checkout" } });

  return (
    <div className="container-max fade-up" style={{ padding: isMobile ? "24px 16px 120px" : "40px 24px 56px" }}>
      <SectionTitle title="Cart" subtitle={`${count} item${count > 1 ? "s" : ""}`} />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.6fr 1fr", gap: isMobile ? 24 : 48 }}>
        <div>
          {items.map(({ product, quantity }) => (
            <div key={product.id} data-testid={`cart-item-${product.id}`} style={{ display: "flex", gap: 16, padding: "16px 0", borderBottom: `1px solid ${colors.border}` }}>
              <Link to={`/product/${product.id}`} style={{ flex: "0 0 auto" }}>
                <div style={{ width: 88, height: 110, overflow: "hidden", border: `1px solid ${colors.border}`, background: colors.surfaceSecondary }}>
                  {product.images?.[0] && <img src={product.images[0]} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
              </Link>
              <div style={{ flex: 1 }}>
                <Link to={`/product/${product.id}`} className="font-body" style={{ fontWeight: 600, textDecoration: "none", color: colors.onSurface }}>{product.name}</Link>
                <div className="font-body" style={{ marginTop: 4, fontWeight: 600 }}>{inr(product.price)}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12 }}>
                  <div style={{ display: "inline-flex", alignItems: "center", border: `1px solid ${colors.borderStrong}`, borderRadius: 999 }}>
                    <button data-testid={`cart-minus-${product.id}`} onClick={() => updateQty(product.id, quantity - 1)} style={stepBtn}><Minus size={14} /></button>
                    <span style={{ minWidth: 30, textAlign: "center", fontWeight: 600 }}>{quantity}</span>
                    <button data-testid={`cart-plus-${product.id}`} onClick={() => updateQty(product.id, quantity + 1)} style={stepBtn}><Plus size={14} /></button>
                  </div>
                  <button data-testid={`cart-remove-${product.id}`} onClick={() => remove(product.id)} style={{ background: "none", border: "none", cursor: "pointer", color: colors.mutedText, display: "flex", alignItems: "center", gap: 5, fontSize: 13 }}>
                    <Trash2 size={15} /> Remove
                  </button>
                </div>
              </div>
              <div className="font-body" style={{ fontWeight: 700 }}>{inr(product.price * quantity)}</div>
            </div>
          ))}
        </div>

        <div>
          <div style={{ background: colors.surfaceSecondary, borderRadius: 12, padding: 24, position: isMobile ? "static" : "sticky", top: 90 }}>
            <h3 className="font-display" style={{ fontSize: 22, fontWeight: 700, margin: "0 0 16px" }}>Summary</h3>
            <Row label="Subtotal" value={inr(subtotal)} />
            <Row label="Shipping" value="Calculated at checkout" muted />
            <div style={{ borderTop: `1px solid ${colors.border}`, margin: "14px 0" }} />
            <Row label="Total" value={inr(subtotal)} bold />
            <button data-testid="checkout-btn" className="btn-brand" style={{ width: "100%", marginTop: 20 }} onClick={goCheckout}>Proceed to Checkout</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const stepBtn = { background: "none", border: "none", cursor: "pointer", padding: "8px 12px", display: "flex", alignItems: "center" };

function Row({ label, value, bold, muted }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
      <span className="font-body" style={{ color: muted ? colors.mutedText : colors.onSurface, fontWeight: bold ? 700 : 400, fontSize: bold ? 17 : 14 }}>{label}</span>
      <span className="font-body" style={{ fontWeight: bold ? 700 : 500, fontSize: bold ? 17 : 14, color: muted ? colors.mutedText : colors.onSurface }}>{value}</span>
    </div>
  );
}
